import { useEffect, useState } from "react";
import { fmtDate } from "@/lib/dates";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Megaphone, Calendar, Plus, Pencil, Trash2, MapPin, Clock } from "lucide-react";

const EMPTY = { kind: "event", title: "", description: "", date: new Date().toISOString().slice(0, 10), time: "", location: "" };

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/announcements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...EMPTY, ...a }); setEditId(a.id); setOpen(true); };

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    try {
      if (editId) { await api.put(`/announcements/${editId}`, form); toast.success("Updated"); }
      else { await api.post("/announcements", form); toast.success("Published"); }
      setOpen(false); load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (id) => { try { await api.delete(`/announcements/${id}`); toast.success("Removed"); load(); } catch { toast.error("Could not remove"); } };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#526050]">{items.length} events & notices · shown on the public site</p>
        <Button data-testid="add-announcement-button" onClick={openNew} className="h-11 bg-[#2D5A27] hover:bg-[#23471F] text-white">
          <Plus className="h-4 w-4 mr-2" /> New
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((a) => (
          <Card key={a.id} className="p-6 bg-white border border-[#E5E0D8] shadow-sm" data-testid={`announcement-card-${a.id}`}>
            <div className="flex items-start justify-between">
              <Badge variant="outline" className={a.kind === "event" ? "bg-[#FEF3C7] text-[#B45309] border-[#f5e0a3]" : "bg-[#EAF2E8] text-[#2D5A27] border-[#cfe3cb]"}>
                {a.kind === "event" ? <Calendar className="h-3 w-3 mr-1" /> : <Megaphone className="h-3 w-3 mr-1" />}{a.kind}
              </Badge>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8 text-[#526050]" onClick={() => openEdit(a)} data-testid={`edit-announcement-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(a.id)} data-testid={`delete-announcement-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <h3 className="mt-3 font-serif text-xl font-semibold text-[#1C241B]">{a.title}</h3>
            <p className="mt-1 text-sm text-[#526050]">{a.description}</p>
            {a.kind === "event" && (
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#7A8A78]">
                {a.date && <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{fmtDate(a.date)}</span>}
                {a.time && <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{a.time}</span>}
                {a.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{a.location}</span>}
              </div>
            )}
          </Card>
        ))}
        {items.length === 0 && <p className="text-[#7A8A78] py-8">Nothing published yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="announcement-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "New"} Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="announcement-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Event (with date)</SelectItem>
                  <SelectItem value="notice">Notice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Title</Label>
              <Input data-testid="announcement-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            {form.kind === "event" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Date</Label>
                  <Input type="date" data-testid="announcement-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Time</Label>
                  <Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="10:00 AM" /></div>
                <div className="col-span-2 space-y-1.5"><Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-announcement-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">{editId ? "Save" : "Publish"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
